import React, {useState, useEffect, useCallback, useRef} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Modal, ModalHeader, ModalBody, ModalFooter, Button} from 'reactstrap';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {Spinner} from '../Spinner';
import {FaIcon} from '../FaIcon';
import {Switch} from '../buttons/Swtich';
import {toast} from 'react-toastify';
import {CHANNEL_INFO_BY_CHANNEL} from '../channels/channels';

// --- Invite Client User Modal ---
const InviteClientUserModal = ({isOpen, toggle, clientId, onInvited}) => {
    const {t} = useTranslation();
    const [email, setEmail] = useState('');
    const [saving, setSaving] = useState(false);
    const [permissions, setPermissions] = useState({
        dashboard: true,
        reports: true,
        connect: true,
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email) return;
        setSaving(true);
        const data = {
            email: email,
            user_type: 'Client',
            can_use_marketing_module: false,
            can_use_manage_accounts_module: false,
            module_marketing_can_assign_clients: false,
            module_manage_accounts_google_manager_id: null,
            module_manage_accounts_can_edit_manager_adspent_limit: false,
            module_manage_accounts_can_create_ad_accounts: false,
            module_manage_accounts_can_give_access_to_ad_accounts: false,
            client_ids: [clientId],
            client_features: {
                dashboard: permissions.dashboard,
                reports: permissions.dashboard, // Tablero incluye reportes
                connect: permissions.connect,
                data_explorer: false,
                insights: false,
            },
        };
        new APIClient(API_URL + 'invitation/send').call('POST', data).then((res) => {
            setSaving(false);
            if (res.status === 'ERROR') {
                alert(res.error_message);
            } else {
                toast.success(t('portal_tab.invite_sent_success'));
                setEmail('');
                onInvited();
                toggle();
            }
        });
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <form onSubmit={handleSubmit}>
                <ModalHeader toggle={toggle}>
                    <FaIcon iconName="user-plus"/>
                    {t('portal_tab.invite_title')}
                </ModalHeader>
                <ModalBody>
                    <div className="modal-section">
                        <div className="form-row">
                            <label>{t('portal_tab.invite_email')}</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="nombre@empresa.com"
                                required
                            />
                        </div>

                        <div className="invite-separator" style={{margin: '1.2em 0'}}/>

                        <label>{t('portal_tab.permissions_label')}</label>
                        <div className="invite-client-features">
                            <div className="invite-feature-row">
                                <div className="invite-feature-info">
                                    <FaIcon iconName="chart-pie" className="invite-feature-icon"/>
                                    <div className="invite-feature-text">
                                        <span className="invite-feature-name">{t('portal_tab.perm_dashboard')}</span>
                                        <span className="invite-feature-desc">{t('portal_tab.perm_dashboard_desc')}</span>
                                    </div>
                                </div>
                                <Switch
                                    name="perm_dashboard"
                                    checked={permissions.dashboard}
                                    onChange={(e) => setPermissions({...permissions, dashboard: e.target.checked})}
                                    style={{margin: 0}}
                                />
                            </div>
                            <div className="invite-feature-row">
                                <div className="invite-feature-info">
                                    <FaIcon iconName="microchip" className="invite-feature-icon"/>
                                    <div className="invite-feature-text">
                                        <span className="invite-feature-name">{t('portal_tab.perm_connect')}</span>
                                        <span className="invite-feature-desc">{t('portal_tab.perm_connect_desc')}</span>
                                    </div>
                                </div>
                                <Switch
                                    name="perm_connect"
                                    checked={permissions.connect}
                                    onChange={(e) => setPermissions({...permissions, connect: e.target.checked})}
                                    style={{margin: 0}}
                                />
                            </div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>{t('portal_tab.cancel')}</Button>
                    {saving ? (
                        <Spinner color="positive small"/>
                    ) : (
                        <input type="submit" value={t('portal_tab.send_invite')} className="btn -alt"/>
                    )}
                </ModalFooter>
            </form>
        </Modal>
    );
};

// --- Permission Badges ---
const PermissionBadges = ({permissions, t}) => {
    const badges = [];
    if (permissions.dashboard) badges.push({label: t('portal_tab.perm_dashboard'), icon: 'chart-pie'});
    if (permissions.connect) badges.push({label: t('portal_tab.perm_connect'), icon: 'microchip'});

    if (badges.length === 0) return <span className="portal-tab-perm-none">—</span>;

    return (
        <span className="portal-tab-perm-badges">
            {badges.map((b, i) => (
                <span key={i} className="portal-tab-perm-badge">
                    <FaIcon iconName={b.icon}/>
                    {b.label}
                </span>
            ))}
        </span>
    );
};

// --- Main Component ---
export const ClientPortalTab = ({clientId}) => {
    const {t} = useTranslation();
    const [config, setConfig] = useState(null);
    const [users, setUsers] = useState(null);
    const [pendingInvitations, setPendingInvitations] = useState([]);
    const [templates, setTemplates] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingTemplate, setSavingTemplate] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [branding, setBranding] = useState({logo_url: '', display_name_for_clients: '', brand_primary_color: '', brand_secondary_color: ''});
    const [savingBranding, setSavingBranding] = useState(false);

    const fetchUsers = useCallback(() => {
        new APIClient(API_URL + `client/${clientId}/portal/users/`).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                setUsers(data.data.users || []);
                setPendingInvitations(data.data.pending_invitations || []);
            }
        });
    }, [clientId]);

    useEffect(() => {
        Promise.all([
            new APIClient(API_URL + `client/${clientId}/portal/config/`).call('GET'),
            new APIClient(API_URL + `client/${clientId}/portal/users/`).call('GET'),
            new APIClient(API_URL + 'reports/templates/user/').call('GET'),
        ]).then(([configRes, usersRes, templatesRes]) => {
            if (configRes?.status === 'OK') {
                setConfig(configRes.data);
                if (configRes.data.branding) {
                    setBranding({
                        logo_url: configRes.data.branding.logo_url || '',
                        display_name_for_clients: configRes.data.branding.display_name_for_clients || '',
                        brand_primary_color: configRes.data.branding.brand_primary_color || '',
                        brand_secondary_color: configRes.data.branding.brand_secondary_color || '',
                    });
                }
            }
            if (usersRes?.status === 'OK') {
                setUsers(usersRes.data.users || []);
                setPendingInvitations(usersRes.data.pending_invitations || []);
            }
            if (templatesRes?.status === 'OK') setTemplates(templatesRes.data.user_templates || []);
            setLoading(false);
        });
    }, [clientId]);

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Click-outside handler para cerrar el dropdown
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpen]);

    const handleTemplateSelect = (templateId) => {
        setDropdownOpen(false);
        setSavingTemplate(true);
        new APIClient(API_URL + `client/${clientId}/portal/config/`).call('PUT', {
            template_id: templateId,
        }).then((data) => {
            setSavingTemplate(false);
            if (data?.status === 'OK') {
                setConfig(prev => ({...prev, portal_template: data.data.portal_template}));
            }
        });
    };

    const handleBrandingChange = (e) => {
        const {name, value} = e.target;
        setBranding(prev => ({...prev, [name]: value}));
    };

    const handleSaveBranding = () => {
        setSavingBranding(true);
        new APIClient(API_URL + `client/${clientId}/portal/config/`).call('PUT', {
            branding: branding,
        }).then((data) => {
            setSavingBranding(false);
            if (data?.status === 'OK') {
                toast.success(t('portal_tab.branding_saved'));
            }
        }).catch(() => {
            setSavingBranding(false);
        });
    };

    const handleRemoveUser = (userId) => {
        if (!window.confirm(t('portal_tab.remove_user_confirm'))) return;
        new APIClient(API_URL + `client/${clientId}/portal/users/${userId}/`).call('DELETE').then((data) => {
            if (data?.status === 'OK') {
                fetchUsers();
            }
        });
    };

    const handleInvited = () => {
        fetchUsers();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        const d = new Date(dateStr);
        return d.toLocaleDateString('es-AR', {day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'});
    };

    if (loading) {
        return (
            <div className="section-body h-items">
                <div className="section-body-item -wider box" style={{padding: '3em', textAlign: 'center'}}>
                    <Spinner color="positive"/>
                </div>
            </div>
        );
    }

    const selectedTemplateId = config?.portal_template?.id || '';
    const selectedTemplateName = config?.portal_template?.name || t('portal_tab.no_template');
    const connectedChannels = config?.connected_channels || [];
    const lastReport = config?.last_report;
    const hasUsers = (users && users.length > 0) || pendingInvitations.length > 0;

    const channelName = (ch) => CHANNEL_INFO_BY_CHANNEL[ch]?.Spanish || ch;

    return (
        <div className="section-body h-items client-workspace-body -compact portal-tab-body">
            <div className="section-body-item -wider portal-tab">
                <div className="portal-tab-layout">
                    <div className="portal-tab-main">
                        {/* Seccion: Reporte del Portal */}
                        <div className="box portal-tab-config">
                            <div className="box-header portal-tab-report-header">
                                <h3 className="box-title">{t('portal_tab.report_section_title')}</h3>
                                <Link
                                    className="btn -negative portal-tab-preview-btn"
                                    to={`/client/${clientId}/preview`}
                                    target="_blank"
                                >
                                    <FaIcon iconName="eye"/>
                                    {t('portal_tab.preview_portal')}
                                </Link>
                            </div>
                            <div className="portal-tab-config-body">
                                <div className="portal-tab-config-row">
                                    <div className="portal-tab-template-field">
                                        <label>{t('portal_tab.template_label')}</label>
                                        <div className="portal-tab-template-select" ref={dropdownRef}>
                                            <div className={`portal-template-dropdown ${dropdownOpen ? '-open' : ''}`}>
                                                <button
                                                    type="button"
                                                    className="portal-template-dropdown-trigger form-control"
                                                    onClick={() => !savingTemplate && setDropdownOpen(!dropdownOpen)}
                                                    disabled={savingTemplate}
                                                >
                                                    <span>{selectedTemplateName}</span>
                                                    <FaIcon iconName="chevron-down"/>
                                                </button>
                                                {dropdownOpen && (
                                                    <div className="portal-template-dropdown-menu">
                                                        <div
                                                            className={`portal-template-dropdown-item ${!selectedTemplateId ? '-active' : ''}`}
                                                            onClick={() => handleTemplateSelect(null)}
                                                        >
                                                            {t('portal_tab.no_template')}
                                                        </div>
                                                        {templates && templates.map(tpl => {
                                                            const missingChannels = (tpl.required_channels || [])
                                                                .filter(ch => !connectedChannels.includes(ch));
                                                            const isDisabled = missingChannels.length > 0;
                                                            return (
                                                                <div
                                                                    key={tpl.id}
                                                                    className={`portal-template-dropdown-item ${isDisabled ? '-disabled' : ''} ${tpl.id === selectedTemplateId ? '-active' : ''}`}
                                                                    onClick={isDisabled ? undefined : () => handleTemplateSelect(tpl.id)}
                                                                >
                                                                    <span>{tpl.name}</span>
                                                                    {tpl.requires_ai && (
                                                                        <span className="portal-template-dropdown-note">
                                                                            {t('portal_tab.template_no_ai_note')}
                                                                        </span>
                                                                    )}
                                                                    {isDisabled && (
                                                                        <span className="portal-template-dropdown-reason">
                                                                            {t('portal_tab.requires_channels', {channels: missingChannels.map(ch => channelName(ch)).join(', ')})}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                            {savingTemplate && <Spinner color="positive small"/>}
                                        </div>
                                    </div>
                                </div>

                                {lastReport && (
                                    <div className="portal-tab-last-report">
                                        <FaIcon iconName="clock"/>
                                        {t('portal_tab.last_report_generated')}: {formatDate(lastReport.generated_at)}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Seccion: Marca para clientes */}
                        <div className="box portal-tab-config">
                            <div className="box-header">
                                <h3 className="box-title">{t('portal_tab.branding_section_title')}</h3>
                            </div>
                            <div className="portal-tab-config-body">
                                <p className="field-footnote" style={{marginBottom: '1em'}}>{t('portal_tab.branding_override_hint')}</p>
                                <div className="form-grid row-col-2">
                                    <div className="form-item">
                                        <label>{t('portal_tab.branding_logo_url')}</label>
                                        <input type="text" name="logo_url" placeholder="https://..."
                                               value={branding.logo_url} onChange={handleBrandingChange} />
                                    </div>
                                    <div className="form-item">
                                        <label>{t('portal_tab.branding_display_name')}</label>
                                        <input type="text" name="display_name_for_clients"
                                               value={branding.display_name_for_clients} onChange={handleBrandingChange} />
                                    </div>
                                </div>
                                <div className="form-grid row-col-2">
                                    <div className="form-item">
                                        <label>{t('portal_tab.branding_primary_color')}</label>
                                        <div className="brand-color-input">
                                            <input type="color"
                                                   value={branding.brand_primary_color || '#FF470F'}
                                                   onChange={e => handleBrandingChange({target: {name: 'brand_primary_color', value: e.target.value}})} />
                                            <input type="text" name="brand_primary_color"
                                                   value={branding.brand_primary_color}
                                                   onChange={handleBrandingChange} placeholder="#FF470F" maxLength={7} />
                                        </div>
                                    </div>
                                    <div className="form-item">
                                        <label>{t('portal_tab.branding_secondary_color')}</label>
                                        <div className="brand-color-input">
                                            <input type="color"
                                                   value={branding.brand_secondary_color || '#440631'}
                                                   onChange={e => handleBrandingChange({target: {name: 'brand_secondary_color', value: e.target.value}})} />
                                            <input type="text" name="brand_secondary_color"
                                                   value={branding.brand_secondary_color}
                                                   onChange={handleBrandingChange} placeholder="#440631" maxLength={7} />
                                        </div>
                                    </div>
                                </div>
                                <div className="submit-row">
                                    {savingBranding ? (
                                        <Spinner color="positive small"/>
                                    ) : (
                                        <button type="button" className="btn -alt" onClick={handleSaveBranding}>
                                            {t('portal_tab.branding_save')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <aside className="portal-tab-side">
                        {/* Seccion: Usuarios con acceso */}
                        <div className="box portal-tab-users">
                            <div className="box-header portal-tab-users-header">
                                <h3 className="box-title">{t('portal_tab.users_section_title')}</h3>
                                <button className="btn -alt" onClick={() => setShowInviteModal(true)}>
                                    <FaIcon iconName="user-plus"/>
                                    {t('portal_tab.invite_button')}
                                </button>
                            </div>
                            <div className="portal-tab-users-body">
                                {!hasUsers ? (
                                    <div className="portal-tab-empty-users">
                                        <FaIcon iconName="users" className="portal-tab-empty-users-icon"/>
                                        <p>{t('portal_tab.no_users')}</p>
                                        <button className="btn -alt -negative" onClick={() => setShowInviteModal(true)}>
                                            <FaIcon iconName="user-plus"/>
                                            {t('portal_tab.invite_first_user')}
                                        </button>
                                    </div>
                                ) : (
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th>{t('portal_tab.col_email')}</th>
                                                <th>{t('portal_tab.col_name')}</th>
                                                <th>{t('portal_tab.col_permissions')}</th>
                                                <th>{t('portal_tab.col_status')}</th>
                                                <th>{t('portal_tab.col_sent_at')}</th>
                                                <th style={{width: '50px'}}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users && users.map(user => (
                                                <tr key={user.id}>
                                                    <td>{user.email}</td>
                                                    <td>{user.name}</td>
                                                    <td><PermissionBadges permissions={user.permissions} t={t}/></td>
                                                    <td><span className="portal-tab-active-badge">{t('portal_tab.active_status')}</span></td>
                                                    <td></td>
                                                    <td>
                                                        <button
                                                            className="portal-tab-remove-btn"
                                                            onClick={() => handleRemoveUser(user.id)}
                                                            title={t('portal_tab.remove_user')}
                                                        >
                                                            <FaIcon iconName="xmark"/>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {pendingInvitations.map(inv => (
                                                <tr key={`inv-${inv.id}`} className="portal-tab-pending-row">
                                                    <td>{inv.email}</td>
                                                    <td></td>
                                                    <td><PermissionBadges permissions={inv.permissions} t={t}/></td>
                                                    <td>
                                                        <span className="portal-tab-pending-badge">
                                                            <FaIcon iconName="clock"/>
                                                            {t('portal_tab.invitation_pending')}
                                                        </span>
                                                    </td>
                                                    <td className="portal-tab-sent-date">{formatDate(inv.sent_at)}</td>
                                                    <td></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            <InviteClientUserModal
                isOpen={showInviteModal}
                toggle={() => setShowInviteModal(false)}
                clientId={clientId}
                onInvited={handleInvited}
            />
        </div>
    );
};
